import { FacturesAddons } from './FacturesAddons.js';
import { ApiError } from '../../api/errors.js';
import { CommandeAddonDialog } from './CommandeAddonDialog.js';
import { useFicheFacturationArtosera } from '../../api/facturationArtosera.js';
import { schemaFicheFacturation, type FicheFacturationSaisie } from '@artosera/shared';
import type { SouscriptionAddonResume } from '@artosera/shared';
import type { NaturePayante } from '../../api/addons.js';
import { formatDate } from '@artosera/shared';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Dialog, Tabs } from '@artosera/ui';
import {
  useAddons,
  useFacturesAddons,
  useSouscriptionsAddons,
  useResiliationAddon,
  useCatalogueAddons,
  useCommanderEssai,
  type AddonCatalogue,
} from '../../api/addons.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Link, useSearchParams, useNavigate } from 'react-router';
import { modeDemonstrationActif } from '../../api/demo.js';
import styles from './ReglagesPage.module.css';

/** Le serveur filtre la liste ; aucune connaissance du catalogue caché ici. */
export function AddonsPage(): JSX.Element {
  const { t, i18n } = useTranslation('regie');
  const { galerieSlug, galerie } = useShellContext();
  const requete = useAddons(galerieSlug, galerieALaction(galerie, 'settings:read'));
  const historique = useFacturesAddons(galerieSlug, galerieALaction(galerie, 'addons:invoices'));
  const marketplaceVisible =
    (!requete.isError && requete.data?.marketplaceVisible === true) ||
    (!historique.isError && historique.data?.marketplaceVisible === true);
  const direction = marketplaceVisible && galerieALaction(galerie, 'addons:billing');
  const souscriptions = useSouscriptionsAddons(galerieSlug, direction);
  const resiliation = useResiliationAddon(galerieSlug);
  const [maintenant] = useState(() => Date.now());
  const [aResilier, setAResilier] = useState<SouscriptionAddonResume | null>(null);
  const factures = marketplaceVisible && galerieALaction(galerie, 'addons:invoices');
  const catalogue = useCatalogueAddons(galerieSlug, direction);
  const fiche = useFicheFacturationArtosera(galerieSlug, direction);
  const navigate = useNavigate();
  const [payante, setPayante] = useState<{
    addon: AddonCatalogue;
    nature: NaturePayante;
    cle: string;
  } | null>(null);
  function ouvrirPayante(addon: AddonCatalogue, nature: NaturePayante) {
    if (!fiche.data) return;
    const saisie = fiche.data.ficheFacturation;
    const champs =
      saisie &&
      (Object.fromEntries(
        Object.entries(saisie).filter(([cle]) =>
          ['raisonSociale', 'adresse', 'siren', 'tvaIntracom', 'emailFacturation'].includes(cle),
        ),
      ) as FicheFacturationSaisie | null);
    if (!schemaFicheFacturation.safeParse(champs).success) {
      void navigate(`/${galerieSlug}/reglages/facturation-artosera`);
      return;
    }
    setPayante({ addon, nature, cle: crypto.randomUUID() });
  }
  const [params, setParams] = useSearchParams();
  const [confirmation, setConfirmation] = useState<{
    addon: AddonCatalogue;
    cle: string;
    versionsPrix: Record<string, number>;
  } | null>(null);
  const commande = useCommanderEssai(galerieSlug);
  const [prixModifie, setPrixModifie] = useState(false);
  function ouvrir(addon: AddonCatalogue) {
    commande.reset();
    setPrixModifie(false);
    const versionsPrix = Object.fromEntries(
      (catalogue.data?.categories.flatMap((categorie) => categorie.addons) ?? [])
        .filter(
          (candidate) =>
            candidate.addon === addon.addon ||
            (addon.boiteReception === false && candidate.addon === 'boite-reception'),
        )
        .map((candidate) => [candidate.addon, candidate.versionPrix]),
    );
    setConfirmation({ addon, cle: crypto.randomUUID(), versionsPrix });
  }
  const catalogueLien = `/${galerieSlug}/reglages/addons?onglet=catalogue`;
  const date = (valeur: string) =>
    formatDate(new Date(valeur), i18n.language === 'en' ? 'en' : 'fr', 'court');
  const prix = (centimes: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(
      centimes / 100,
    );
  const liste = (
    <div>
      {requete.isError ? <p role="alert">{t('reglages.addons.erreur')}</p> : null}
      {souscriptions.isError ? <p role="alert">{t('reglages.souscriptions.erreur')}</p> : null}
      <ul>
        {!requete.isError &&
          requete.data?.addons.map((droit) => (
            <li key={droit.addon}>
              <h2>{t(`commun:modules.${droit.addon}.libelle`)}</h2>
              <p>
                {t(`reglages.addons.modes.${droit.mode}`)} · {t(`reglages.addons.${droit.etat}`)} ·{' '}
                {droit.jusquAu
                  ? formatDate(
                      new Date(droit.jusquAu),
                      i18n.language === 'en' ? 'en' : 'fr',
                      'court',
                    )
                  : t('reglages.addons.sansEcheance')}
              </p>
              {direction &&
                souscriptions.data?.items
                  ?.filter((s) => s.addon === droit.addon)
                  .map((s) => (
                    <div key={s.id}>
                      <p>{t(`reglages.souscriptions.${s.etat}`, { date: date(s.finPeriode) })}</p>
                      <p>
                        {t('reglages.souscriptions.prochaine', {
                          date: date(s.finPeriode),
                          prix: prix(s.prochainHtCentimes),
                        })}
                      </p>
                      {(s.etat === 'active' || s.etat === 'resiliee') &&
                      +new Date(s.finPeriode) > maintenant ? (
                        <Button
                          disabled={resiliation.isPending || modeDemonstrationActif()}
                          onClick={() => {
                            resiliation.reset();
                            setAResilier(s);
                          }}
                        >
                          {t(
                            s.etat === 'active'
                              ? 'reglages.souscriptions.resilier'
                              : 'reglages.souscriptions.annulerResiliation',
                          )}
                        </Button>
                      ) : null}
                    </div>
                  ))}
              {droit.masqueLe ? (
                <p>
                  {t('reglages.marketplace.masqueLe', { date: date(droit.masqueLe) })}{' '}
                  <Link to={catalogueLien}>{t('reglages.marketplace.voirCatalogue')}</Link>
                </p>
              ) : null}
              {droit.echeanceProche ? (
                <Badge tone="reserved">{t('reglages.addons.echeanceProche')}</Badge>
              ) : null}
            </li>
          ))}
      </ul>
    </div>
  );
  const payantDesactive = (addon: AddonCatalogue) =>
    modeDemonstrationActif() ||
    !fiche.data ||
    !!addon.dependanceManquante ||
    (addon.etat === 'actif' && (addon.mode === 'achat' || addon.mode === 'offert'));
  const vitrine = (
    <div>
      {fiche.isError ? <p role="alert">{t('reglages.marketplace.devisIndisponible')}</p> : null}
      {prixModifie ? <p role="alert">{t('reglages.marketplace.prixModifie')}</p> : null}
      {catalogue.isPending ? <p role="status">{t('reglages.marketplace.chargement')}</p> : null}
      {catalogue.isError ? <p role="alert">{t('reglages.addons.erreur')}</p> : null}
      {!catalogue.isError &&
        catalogue.data?.categories.map((categorie) => (
          <section key={categorie.categorie}>
            <h2>{t(`reglages.marketplace.categories.${categorie.categorie}`)}</h2>
            {categorie.addons.map((addon) => (
              <article key={addon.addon}>
                <h3>{t(`commun:modules.${addon.addon}.libelle`)}</h3>
                <p>{t(`commun:modules.${addon.addon}.description`)}</p>
                <Badge>{t(`reglages.marketplace.etats.${addon.etat}`)}</Badge>
                {addon.masqueLe ? (
                  <p>{t('reglages.marketplace.masqueLe', { date: date(addon.masqueLe) })}</p>
                ) : null}
                {addon.mode ? (
                  <p>
                    {t(`reglages.addons.modes.${addon.mode}`)} ·{' '}
                    {addon.jusquAu
                      ? formatDate(
                          new Date(addon.jusquAu),
                          i18n.language === 'en' ? 'en' : 'fr',
                          'court',
                        )
                      : t('reglages.addons.sansEcheance')}
                  </p>
                ) : null}
                <ul>
                  {(
                    [
                      ['mensuel', addon.mensuelHtCentimes],
                      ['annuel', addon.annuelHtCentimes],
                      ['achat', addon.achatHtCentimes],
                    ] as const
                  )
                    .filter(([, montant]) => montant !== undefined)
                    .map(([formule, montant]) => (
                      <li key={formule}>
                        {t(`reglages.marketplace.${formule}`)} : {prix(montant!)}{' '}
                        {t('reglages.marketplace.ht')}
                      </li>
                    ))}
                </ul>
                {addon.essaiJours > 0 ? (
                  <p>{t('reglages.marketplace.essai', { jours: addon.essaiJours })}</p>
                ) : null}
                {addon.dependanceManquante ? (
                  <p>{t('reglages.marketplace.facturationRequise')}</p>
                ) : null}
                {addon.boiteReception !== undefined ? (
                  <p>
                    {t(
                      addon.boiteReception
                        ? 'reglages.marketplace.boiteActive'
                        : 'reglages.marketplace.boiteRequise',
                    )}
                  </p>
                ) : null}
                {addon.essaiJours > 0 ? (
                  <Button
                    disabled={
                      modeDemonstrationActif() ||
                      addon.etat !== 'disponible' ||
                      addon.dejaEssaye ||
                      !!addon.dependanceManquante
                    }
                    onClick={() => ouvrir(addon)}
                  >
                    {t('reglages.marketplace.essayer', { jours: addon.essaiJours })}
                  </Button>
                ) : null}
                {addon.mensuelHtCentimes !== undefined ? (
                  <Button
                    disabled={
                      payantDesactive(addon) ||
                      (addon.etat === 'actif' && addon.mode === 'location')
                    }
                    onClick={() => ouvrirPayante(addon, 'location_mensuelle')}
                  >
                    {t('reglages.marketplace.louerMensuel')}
                  </Button>
                ) : null}
                {addon.annuelHtCentimes !== undefined ? (
                  <Button
                    disabled={
                      payantDesactive(addon) ||
                      (addon.etat === 'actif' && addon.mode === 'location')
                    }
                    onClick={() => ouvrirPayante(addon, 'location_annuelle')}
                  >
                    {t('reglages.marketplace.louerAnnuel')}
                  </Button>
                ) : null}
                {addon.achatHtCentimes !== undefined ? (
                  <Button
                    disabled={payantDesactive(addon)}
                    onClick={() => ouvrirPayante(addon, 'achat')}
                  >
                    {t('reglages.marketplace.acheter')}
                  </Button>
                ) : null}
              </article>
            ))}
          </section>
        ))}
      {!catalogue.isError && catalogue.data?.categories.length === 0 ? (
        <p>{t('reglages.marketplace.vide')}</p>
      ) : null}
    </div>
  );
  return (
    <div className={styles.page}>
      <h1 className={styles.titre}>{t('reglages.addons.titre')}</h1>
      {direction || factures ? (
        <Tabs
          value={direction ? (params.get('onglet') ?? 'mes-addons') : 'factures'}
          onValueChange={(value) => setParams({ onglet: value })}
          tabs={[
            ...(direction
              ? [
                  {
                    value: 'mes-addons',
                    label: t('reglages.marketplace.mesAddons'),
                    content: liste,
                  },
                  {
                    value: 'catalogue',
                    label: t('reglages.marketplace.catalogue'),
                    content: vitrine,
                  },
                ]
              : []),
            ...(factures
              ? [
                  {
                    value: 'factures',
                    label: t('reglages.facturesAddons.titre'),
                    content: <FacturesAddons />,
                  },
                ]
              : []),
          ]}
        />
      ) : (
        liste
      )}
      <Dialog
        open={aResilier !== null}
        onOpenChange={(open) => {
          if (!open && !resiliation.isPending) setAResilier(null);
        }}
        title={t('reglages.souscriptions.titre')}
        closeLabel={t('reglages.marketplace.annuler')}
      >
        {aResilier ? (
          <>
            <p>{t('reglages.souscriptions.confirmation', { date: date(aResilier.finPeriode) })}</p>
            {resiliation.isError ? <p role="alert">{t('reglages.souscriptions.erreur')}</p> : null}
            <Button
              disabled={resiliation.isPending}
              onClick={() =>
                resiliation.mutate(
                  {
                    souscription: aResilier,
                    geste: aResilier.etat === 'active' ? 'resilier' : 'annuler-resiliation',
                  },
                  { onSuccess: () => setAResilier(null) },
                )
              }
            >
              {t('reglages.souscriptions.confirmer')}
            </Button>
          </>
        ) : null}
      </Dialog>
      {payante ? (
        <CommandeAddonDialog
          key={payante.cle}
          galerie={galerieSlug}
          {...payante}
          fermer={() => setPayante(null)}
        />
      ) : null}
      <Dialog
        open={confirmation !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert && !commande.isPending) setConfirmation(null);
        }}
        title={
          confirmation
            ? t(`commun:modules.${confirmation.addon.addon}.libelle`)
            : t('reglages.addons.titre')
        }
        closeLabel={t('reglages.marketplace.annuler')}
      >
        {confirmation ? (
          <>
            <p>{t('reglages.marketplace.essai', { jours: confirmation.addon.essaiJours })}</p>
            <p>{t('reglages.marketplace.sansEngagement')}</p>
            {confirmation.addon.boiteReception === false ? (
              <p>{t('reglages.marketplace.dependanceIncluse')}</p>
            ) : null}
            {commande.isError ? <p role="alert">{t('reglages.marketplace.echecEssai')}</p> : null}
            <Button
              disabled={commande.isPending}
              onClick={() =>
                commande.mutate(
                  {
                    addon: confirmation.addon.addon,
                    cle: confirmation.cle,
                    versionsPrix: confirmation.versionsPrix,
                    avecDependances: confirmation.addon.boiteReception === false,
                  },
                  {
                    onSuccess: () => setConfirmation(null),
                    onError: (erreur) => {
                      if (
                        erreur instanceof ApiError &&
                        erreur.status === 409 &&
                        (erreur.details as { raison?: string } | null)?.raison === 'prix_modifie'
                      ) {
                        setConfirmation(null);
                        setPrixModifie(true);
                      }
                    },
                  },
                )
              }
            >
              {t('reglages.marketplace.confirmerEssai')}
            </Button>
            <Button
              variant="secondary"
              disabled={commande.isPending}
              onClick={() => setConfirmation(null)}
            >
              {t('reglages.marketplace.annuler')}
            </Button>
          </>
        ) : null}
      </Dialog>
    </div>
  );
}
